import { operationIds } from "@gen/app-access-config";
import type { IAdminTransaction, ITransaction } from "@gen/app-query";
import MuiLink from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Link } from "@tanstack/react-router";

import { HeadingContainer } from "~/components/heading_container";
import { LedgerAmount } from "~/components/ledger_amount";
import { PublicSvgIcon } from "~/components/public_svg_icon";
import { useCanExecuteOperation } from "~/contexts/auth_query";
import { useConfig } from "~/contexts/config";
import { isAdminTransaction, TransactionMoney } from "~/entities/transaction";
import { getPayoutURL, getTransferURL } from "~/services/stripe";

import { StripeFundsReleaseStatus } from "./StripeFundsReleaseStatus";

export function SellerLedger({
  transactionData,
  allocateExtraPriceToClient,
  allocatePostagePriceTo,
}: {
  transactionData: ITransaction | IAdminTransaction;
  allocateExtraPriceToClient?: boolean;
  allocatePostagePriceTo?: string;
}) {
  const canExecuteOperation = useCanExecuteOperation();
  const { stripe: stripe, localization } = useConfig();

  const isAdminTx = isAdminTransaction(transactionData);

  const canGetPayoutDetails = canExecuteOperation(
    isAdminTx
      ? operationIds["core_admin.getAdminPayoutDetails"]
      : operationIds["core.getPayoutDetails"],
  );
  const transactionMoney = new TransactionMoney(transactionData);

  const remainderTransferId = isAdminTx
    ? transactionData.remainder_stripe_transfer_id
    : undefined;

  const stripeTransferId = isAdminTx
    ? transactionData.stripe_transfer_id ||
      transactionData.deposit_stripe_transfer_id ||
      remainderTransferId
    : undefined;

  const stripePayoutId = isAdminTx
    ? transactionData.funds_release?.payout_summary?.stripe_payout_id
    : undefined;

  // Both payout details pages are keyed by the Trustap payout ID; the admin
  // transaction carries it under the funds release rather than at the top
  // level, and only f2f releases have one at all.
  const payoutId = isAdminTx
    ? transactionData.funds_release?.payout_summary?.payout_id
    : transactionData.payout_id;

  const payoutsBasePath = isAdminTx
    ? "/admin/payouts"
    : `/clients/${transactionData.client_id}/payouts`;

  const payoutDetailsURL =
    payoutId === undefined ? undefined : `${payoutsBasePath}/${payoutId}`;

  const capitalizeFirstLetter = (str: string) => {
    return str[0].toUpperCase() + str.slice(1);
  };

  const renderNonRefundedLinks = () => {
    if (!isAdminTx) {
      return null;
    }

    const stripePlatformId = isAdminTx
      ? transactionData.stripe_platform_id
      : undefined;

    const stripeAccountId = transactionData.seller_stripe_account_id;

    const hasDepositFundsRelease =
      transactionData.funds_release?.deposit?.url &&
      transactionData.funds_release?.deposit?.type;

    const hasRemainderFundsRelease =
      transactionData.funds_release?.remainder?.url &&
      transactionData.funds_release?.remainder?.type;

    const hasTransferFallback =
      !transactionData.funds_release && stripeTransferId;

    return (
      <>
        {hasDepositFundsRelease &&
          transactionData.funds_release?.deposit?.url && (
            <MuiLink
              href={transactionData.funds_release.deposit.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Stripe{" "}
              {capitalizeFirstLetter(
                transactionData.funds_release.deposit.type ?? "",
              )}
            </MuiLink>
          )}
        {hasRemainderFundsRelease &&
          transactionData.funds_release?.remainder?.url && (
            <MuiLink
              href={transactionData.funds_release.remainder.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Stripe{" "}
              {capitalizeFirstLetter(
                transactionData.funds_release.remainder.type ?? "",
              )}
            </MuiLink>
          )}
        {hasTransferFallback && (
          <MuiLink
            href={getTransferURL(stripe, stripePlatformId, stripeTransferId)}
            target="_blank"
            rel="noopener noreferrer"
          >
            Stripe Transfer
          </MuiLink>
        )}
        {stripePayoutId && stripeAccountId && (
          <MuiLink
            href={getPayoutURL(
              stripe,
              stripePlatformId,
              stripeAccountId,
              stripePayoutId,
            )}
            target="_blank"
            rel="noopener noreferrer"
          >
            Stripe Payout
          </MuiLink>
        )}
      </>
    );
  };

  const renderFundsReleaseStatus = () => {
    if (stripeTransferId || remainderTransferId) {
      const fundsRelease = isAdminTx
        ? transactionData.funds_release
        : undefined;
      return (
        <StripeFundsReleaseStatus
          id={(stripeTransferId || remainderTransferId)!}
          label={
            fundsRelease?.deposit?.label ||
            fundsRelease?.remainder?.label ||
            "Transfer Details"
          }
          type={
            fundsRelease?.deposit?.type ||
            fundsRelease?.remainder?.type ||
            "transfer"
          }
        />
      );
    }

    return null;
  };

  const fundsReleased = transactionData.funds_released;
  const isReleased = !!fundsReleased;
  const hasRefunds = !transactionMoney
    .getAmountRefundedBeforeRelease()
    .isZero();
  const postReleaseRefunds = transactionMoney.getPostReleaseRefunds();
  const isCancelled = !!transactionData.cancelled;

  const isExtraPriceAllocatedToSeller = (() => {
    if (transactionMoney.getPriceExtra().isZero()) {
      return false;
    }

    // If the transaction is released, we are determining if the extra price was allocated to the seller
    // by checking if the payout is equal to the expected payout with the extra price.
    if (isReleased) {
      return transactionMoney
        .getSellerPayout()
        .subtract(transactionMoney.getExpectedPayout(true))
        .isZero();
    }

    return allocateExtraPriceToClient ?? false;
  })();

  const isPostageAllocatedToSeller =
    !transactionMoney.getPricePostage().isZero() &&
    allocatePostagePriceTo === "seller";

  let payoutHeading = "Expected Payout";
  let payoutLabel = "Expected Payout";
  let payoutValue = transactionMoney.getExpectedPayout(
    isExtraPriceAllocatedToSeller,
    isPostageAllocatedToSeller,
  );

  if (isCancelled) {
    payoutLabel = "Amount Released";
    payoutValue = transactionMoney.zero();
  } else if (fundsReleased) {
    payoutHeading =
      "Funds Released " +
      fundsReleased
        .toLocaleString(localization.locale, {
          timeZone: localization.timeZone,
        })
        .split(", ")[0];
    payoutLabel = "Amount Released";
    payoutValue = transactionMoney.getSellerPayout();
  } else if (hasRefunds) {
    payoutLabel = "Amount Released";
    payoutValue = payoutValue.isNegative()
      ? transactionMoney.zero()
      : payoutValue;
  }

  return (
    <Stack
      sx={{
        backgroundColor: "background.default",
        border: "1px solid",
        borderColor: "gray.200",
        borderRadius: 2,
        p: 3,
      }}
      flexDirection="column"
      spacing={3}
    >
      <Stack spacing={1}>
        <HeadingContainer
          title={payoutHeading}
          variant="body1"
          borderType="dashed"
        >
          <Stack direction="row" spacing={3} alignItems="center">
            {!transactionData?.refunded && renderNonRefundedLinks()}
          </Stack>
        </HeadingContainer>
        <Stack flexDirection="row" justifyContent="space-between">
          <Typography variant="body2">Price</Typography>
          <LedgerAmount
            amount={transactionMoney.getPrice()}
            data-testid="seller-ledger-price-text"
          />
        </Stack>
        {isExtraPriceAllocatedToSeller && (
          <Stack flexDirection="row" justifyContent="space-between">
            <Typography variant="body2">Price (extra)</Typography>
            <LedgerAmount
              amount={transactionMoney.getPriceExtra()}
              data-testid="seller-ledger-price_extra-text"
            />
          </Stack>
        )}
        {isPostageAllocatedToSeller && (
          <Stack flexDirection="row" justifyContent="space-between">
            <Typography variant="body2">Postage fee</Typography>
            <LedgerAmount
              amount={transactionMoney.getPricePostage()}
              data-testid="seller-ledger-price_postage-text"
            />
          </Stack>
        )}
        {transactionMoney.getPostageBearer() === "seller" &&
          !transactionMoney.getPricePostage().isZero() && (
            <Stack flexDirection="row" justifyContent="space-between">
              <Typography variant="body2">Postage fee</Typography>
              <LedgerAmount
                amount={transactionMoney.getPricePostage().negate()}
                data-testid="seller-ledger-postage_charge-text"
              />
            </Stack>
          )}
        {!transactionMoney.getSellerFees().isZero() && (
          <Stack flexDirection="row" justifyContent="space-between">
            <Typography variant="body2">Trustap fee</Typography>
            <LedgerAmount
              amount={transactionMoney.getSellerFees().negate()}
              data-testid="seller-ledger-charge_seller-text"
            />
          </Stack>
        )}
        {!transactionMoney.getChargeSellerClient().isZero() && (
          <Stack flexDirection="row" justifyContent="space-between">
            <Typography variant="body2">Platform commission</Typography>
            <LedgerAmount
              amount={transactionMoney.getChargeSellerClient().negate()}
              data-testid="seller-ledger-charge_seller_client-text"
            />
          </Stack>
        )}
        {!transactionMoney.getInternationalFees().isZero() && (
          <Stack flexDirection="row" justifyContent="space-between">
            <Typography variant="body2">International fees</Typography>
            <LedgerAmount
              amount={transactionMoney.getInternationalFees().negate()}
              data-testid="seller-ledger-charge_international_payment-text"
            />
          </Stack>
        )}
        {hasRefunds && (
          <Stack flexDirection="row" justifyContent="space-between">
            <Typography variant="body2">Refunded</Typography>
            <LedgerAmount
              amount={transactionMoney
                .getAmountRefundedBeforeRelease()
                .negate()}
              data-testid="seller-ledger-amount_refunded-text"
            />
          </Stack>
        )}
        {renderFundsReleaseStatus()}
      </Stack>

      <Stack spacing={1}>
        <Stack flexDirection="row" justifyContent="space-between">
          <Typography variant="body2">{payoutLabel}</Typography>
          <LedgerAmount
            amount={payoutValue}
            variant="sumUpLine"
            data-testid="seller-payout-text"
          />
        </Stack>
        {payoutDetailsURL && canGetPayoutDetails && (
          <Stack flexDirection="row" justifyContent="flex-end">
            <MuiLink
              component={Link}
              to={payoutDetailsURL}
              data-testid="seller-ledger-payout-details-link"
            >
              View Payout Details
            </MuiLink>
          </Stack>
        )}
      </Stack>

      {postReleaseRefunds.map((refund) => (
        <Stack spacing={1} key={refund.id}>
          <HeadingContainer
            title={`Refund ${
              refund.refunded
                .toLocaleString(localization.locale, {
                  timeZone: localization.timeZone,
                })
                .split(", ")[0]
            }`}
            variant="body1"
            borderType="dashed"
          />
          <Stack flexDirection="row" justifyContent="space-between">
            <Typography variant="body2">Refund (post release)</Typography>
            <LedgerAmount
              amount={refund.amount.negate()}
              data-testid={`seller-ledger-post_release_refund-${refund.id}-text`}
            />
          </Stack>
        </Stack>
      ))}

      {postReleaseRefunds.length > 0 && (
        <Stack flexDirection="row" justifyContent="space-between">
          <Stack flexDirection="row" alignItems="center">
            <Typography variant="body2">
              Future Payout Reduction&nbsp;
            </Typography>
            <Tooltip title="Amounts refunded after funds were released will be deducted from your future payouts. Capped at the amount released to the seller for this transaction.">
              <PublicSvgIcon name="info" />
            </Tooltip>
          </Stack>
          <LedgerAmount
            amount={transactionMoney
              .getAmountRefundedPostRelease()
              .add(transactionMoney.getRefundsAccrualNetDelta())
              .negate()}
            variant="sumUpLine"
            data-testid="seller-ledger-future_payout_reduction-text"
          />
        </Stack>
      )}
    </Stack>
  );
}
